/**
 * CONTACT PAGE
 * 
 * Features:
 * - Category dropdown for routing inquiries
 * - Form validation
 * - Success/error states
 * - Matches app styling
 * 
 * NOTE: Requires email service setup (EmailJS, Formspree, etc.)
 */

import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { PageHeader } from '../components/common';

// Contact form categories
const CATEGORIES = [
  { value: '', label: 'Select a category...' },
  { value: 'support', label: '💬 General Support' },
  { value: 'account', label: '🔐 Account Help' },
  { value: 'marketplace', label: '🛒 Marketplace Help' },
  { value: 'bug', label: '🐛 Report a Bug' },
  { value: 'feature', label: '💡 Request a Feature' },
  { value: 'report', label: '🚩 Report User/Content' },
  { value: 'privacy', label: '🔒 Privacy Request' },
  { value: 'legal', label: '⚖️ Legal Inquiry' },
  { value: 'business', label: '🤝 Business/Partnerships' },
  { value: 'feedback', label: '💭 General Feedback' },
  { value: 'other', label: '📝 Other' },
];

export default function ContactPage() {
  const navigate = useNavigate();
  
  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Form validation
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isFormValid = name.trim() && isValidEmail(email) && category && message.trim();

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Send via EmailJS
      await emailjs.send(
        'service_mspkyt5',
        'template_vjfdxqo',
        {
          from_name: name,
          from_email: email,
          category: CATEGORIES.find(c => c.value === category)?.label || category,
          message: message,
        },
        'TnFGInG6cAnf9y9vV'
      );

      setSubmitStatus('success');
      // Clear form
      setName('');
      setEmail('');
      setCategory('');
      setMessage('');
      
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus('error');
      setErrorMessage('Failed to send message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <PageHeader
          icon={Send}
          title="Contact Us"
          subtitle="Have a question, feedback, or need help? We'd love to hear from you!"
          className="mb-6"
        />

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className="glass-card-sm p-4 mb-6 flex items-start gap-3 border-l-4 border-l-green-500">
            <CheckCircle className="icon-base text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-glass font-body">
                Message Sent!
              </h3>
              <p className="text-glass-secondary text-sm mt-1">
                Thank you for reaching out! We'll get back to you as soon as possible.
              </p>
              <p className="text-glass-muted text-sm mt-2 flex items-center gap-1">
                <strong>Tip:</strong> Please check your spam/junk folder for our reply!
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <div className="glass-card-sm p-4 mb-6 flex items-start gap-3 border-l-4 border-l-red-500">
            <AlertCircle className="icon-base text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-glass font-body">
                Oops! Something went wrong
              </h3>
              <p className="text-glass-secondary text-sm mt-1">
                {errorMessage}
              </p>
            </div>
          </div>
        )}

        {/* Contact Form */}
        <div className="glass-card overflow-hidden">
          <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
            {/* Name */}
            <div>
              <label 
                htmlFor="name" 
                className="block text-sm font-bold text-glass-secondary mb-2 font-body"
              >
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="w-full px-4 py-3 border border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all bg-slate-800/60 text-glass placeholder:text-glass-muted font-body"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label 
                htmlFor="email" 
                className="block text-sm font-bold text-glass-secondary mb-2 font-body"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all bg-slate-800/60 text-glass placeholder:text-glass-muted font-body ${
                  email && !isValidEmail(email) ? 'border-red-300' : 'border-slate-600'
                }`}
                required
              />
              {email && !isValidEmail(email) && (
                <p className="mt-1 text-sm text-red-400">Please enter a valid email address</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label 
                htmlFor="category" 
                className="block text-sm font-bold text-glass-secondary mb-2 font-body"
              >
                What can we help you with? <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={`w-full px-4 py-3 border border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all appearance-none bg-slate-800/60 font-body ${
                  !category ? 'text-glass-secondary' : 'text-glass'
                }`}
                required
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div>
              <label 
                htmlFor="message" 
                className="block text-sm font-bold text-glass-secondary mb-2 font-body"
              >
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us more about how we can help..."
                rows={5}
                className="w-full px-4 py-3 border border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all resize-none bg-slate-800/60 text-glass placeholder:text-glass-muted font-body"
                required
              />
              <p className="mt-1 text-sm text-glass-secondary text-right">
                {message.length} characters
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white rounded-full font-bold text-lg hover:from-fuchsia-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg font-body"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="icon-md" />
                  <span>Send Message</span>
                </>
              )}
            </button>
          </form>

          {/* Footer Note */}
          <div className="px-6 md:px-8 py-4 bg-slate-700/50 border-t border-slate-600">
            <p className="text-sm text-glass-secondary text-center font-body">
              We typically respond within 24-48 hours. For urgent matters, please indicate so in your message.
            </p>
          </div>
        </div>

        {/* Discord Link */}
        <div className="mt-8 glass-card p-6 text-center">
          <p className="text-glass-secondary font-body mb-3">
            Want to discuss updates or share feedback?
          </p>
          <a
          href="https://discord.gg/QwDyUDwHdz"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#5865F2] text-white rounded-lg font-medium hover:bg-[#4752C4] transition-default font-body"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Join our Discord
          </a>
        </div>

        {/* FAQ Teaser */}
        <div className="mt-8 text-center">
          <p className="text-on-bg font-body">
            Looking for quick answers? Check out our{' '}
            <button
              onClick={() => navigate('/help')}
              className="text-on-bg font-semibold underline underline-offset-2"
            >
              FAQ page
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
